/* oxlint-disable react/no-unstable-nested-components -- LINTER migration follow up */

import { authSignOut } from "@grafit/frontend-auth/auth-client";
import { AuthProvider } from "@grafit/frontend-auth/auth-provider";
import { getPinnedSessionId } from "@grafit/frontend-auth/pinned-session-id";
import { useTranslation } from "@grafit/i18n";
import { ErrorBoundary, log } from "@grafit/shared/observability/sentry-front";
import { getAppVersion } from "@grafit/shared/utils/version";
import { HEADER_FRONTEND_APP_VERSION, HEADER_PINNED_SESSION_ID } from "@grafit/shared-api-router/services/logger/types";
import { HotkeysProvider } from "@tanstack/react-hotkeys";
import { focusManager, MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { MotionConfig } from "motion/react";
import { memo, useEffect, useMemo } from "react";
import superjson from "superjson";
import { handleAppVersionCheck } from "../features/app-update/app-update";
import { assessBackendReachability, setBackendReachable } from "../utils/backend-reachability";
import { httpBatchLink, httpSubscriptionLink, splitLink, trpc } from "../utils/trpc";
import {
  isApp400Error,
  isProjectDeletedError,
  isSessionChangedError,
  isTRPC40XError,
  isUnauthorized,
  isVersionReadOnlyError,
} from "../utils/trpc-error-helpers";
import { TRPC_URL } from "./constants";
import { RootErrorFallback } from "./root-error-fallback";
import { router } from "./router";
import { RouterProvider } from "./router-api";
import { TrpcErrorNotificationProvider, useNotifyTrpcError } from "./trpc-error-notification-provider";
import { useObservabilityWithUserProfile } from "./use-observability-with-user-profile";

const MAX_RETRY_COUNT = 3;

function useReactQueryFocusEvents(): void {
  useEffect(() => {
    const setFocusState = (isFocused: boolean): void => {
      // React Query's default visibilitychange listener misses OS-level window refocus.
      // Defer the focus update to avoid React flushSync warnings from lifecycle-triggered events.
      queueMicrotask(() => {
        focusManager.setFocused(isFocused);
      });
    };
    const setWindowFocused = (): void => setFocusState(true);
    const setWindowBlurred = (): void => setFocusState(false);
    const setVisibilityFocused = (): void => setFocusState(document.visibilityState === "visible");

    window.addEventListener("focus", setWindowFocused, false);
    window.addEventListener("blur", setWindowBlurred, false);
    window.addEventListener("pageshow", setWindowFocused, false);
    window.addEventListener("pagehide", setWindowBlurred, false);
    document.addEventListener("visibilitychange", setVisibilityFocused, false);

    return () => {
      window.removeEventListener("focus", setWindowFocused);
      window.removeEventListener("blur", setWindowBlurred);
      window.removeEventListener("pageshow", setWindowFocused);
      window.removeEventListener("pagehide", setWindowBlurred);
      document.removeEventListener("visibilitychange", setVisibilityFocused);
      focusManager.setFocused(undefined);
    };
  }, []);
}

let isReloadingStaleTab = false;

/** A batch can fail several queries at once: reload only once. */
const reloadStaleTab = (): void => {
  if (isReloadingStaleTab) {
    return;
  }
  isReloadingStaleTab = true;
  log("[Auth] Session changed in another tab, reloading", "warn");
  window.location.reload();
};

const handleMutationError = ({
  error,
  queryClient,
  notifyError,
}: {
  error: Error;
  queryClient: QueryClient;
  notifyError: ReturnType<typeof useNotifyTrpcError>;
}): void => {
  if (isUnauthorized(error)) {
    log(`[Auth SignOut] Unauthorized mutation error: ${error.message}`, "warn");
    void authSignOut();
  }
  if (isSessionChangedError(error)) {
    reloadStaleTab();
  }
  if (isVersionReadOnlyError(error)) {
    notifyError("version-read-only");
  }
  if (isProjectDeletedError(error)) {
    // The refetch fails with the same error, so the project route swaps to its "deleted" page.
    void queryClient.invalidateQueries({ queryKey: getQueryKey(trpc.projects.getByIdWithContent) });
  }
  assessBackendReachability(error, "mutation");
};

/** @internal (exported for tests) */
export const TRPCProviderWrapper = memo(({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const notifyError = useNotifyTrpcError();
  useReactQueryFocusEvents();

  const queryClient = useMemo(() => {
    const client: QueryClient = new QueryClient({
      queryCache: new QueryCache({
        onError: (error) => {
          if (isUnauthorized(error)) {
            log(`[Auth SignOut] Unauthorized query error: ${error.message}`, "warn");
            void authSignOut();
          }
          if (isSessionChangedError(error)) {
            reloadStaleTab();
          }
          assessBackendReachability(error, "query");
        },
      }),
      mutationCache: new MutationCache({
        onError: (error) => handleMutationError({ error, queryClient: client, notifyError }),
      }),
      defaultOptions: {
        queries: {
          networkMode: "always",
          retry: (failureCount, error) =>
            isTRPC40XError(error) || isApp400Error(error) ? false : failureCount < MAX_RETRY_COUNT,
        },
        mutations: {
          networkMode: "online",
          retry: false,
        },
      },
    });
    return client;
  }, [notifyError]);

  const trpcClient = useMemo(
    () =>
      trpc.createClient({
        links: [
          splitLink({
            condition: (op) => op.type === "subscription",
            true: httpSubscriptionLink({
              url: TRPC_URL,
              transformer: superjson,
              eventSourceOptions: { withCredentials: true },
            }),
            false: httpBatchLink({
              url: TRPC_URL,
              transformer: superjson,
              fetch: async (url, options) => {
                const headers = new Headers(options?.headers);
                headers.set(HEADER_FRONTEND_APP_VERSION, getAppVersion());
                const pinnedSessionId = getPinnedSessionId();
                if (pinnedSessionId) {
                  headers.set(HEADER_PINNED_SESSION_ID, pinnedSessionId);
                }
                const response = await fetch(url, {
                  ...(options as RequestInit),
                  credentials: "include",
                  headers,
                });
                handleAppVersionCheck(response.headers, t);
                setBackendReachable();
                return response;
              },
            }),
          }),
        ],
      }),
    [t],
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
});

TRPCProviderWrapper.displayName = "TRPCProviderWrapper";

export const Providers = () => (
  <ErrorBoundary fallback={({ error }) => <RootErrorFallback error={error} />} handled={false}>
    <MotionConfig reducedMotion="user">
      <AuthProvider>
        <TrpcErrorNotificationProvider>
          <TRPCProviderWrapper>
            <SetupObservability />
            <HotkeysProvider>
              <RouterProvider router={router} />
            </HotkeysProvider>
          </TRPCProviderWrapper>
        </TrpcErrorNotificationProvider>
      </AuthProvider>
    </MotionConfig>
  </ErrorBoundary>
);

function SetupObservability() {
  useObservabilityWithUserProfile();
  return null;
}
